/**
 * CurseActiveTab - Componenta pentru tab-ul Curse Active
 * Monitorizare în timp real a curselor active + Calendar istoric curse
 */
import { memo, useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Map, Search, X, CalendarDays, Clock, CheckCircle, ChevronDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const CurseActiveTab = memo(function CurseActiveTab({
  activeTrips,
  todayTrips = [],
  cars = [],
  handleOpenCloseDialog,
  loading,
  auth
}) {
  const [searchText, setSearchText] = useState('');
  
  // Calendar state
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateTrips, setDateTrips] = useState([]);
  const [loadingDateTrips, setLoadingDateTrips] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Fetch trips for selected date
  useEffect(() => {
    if (selectedDate) {
      fetchTripsForDate(selectedDate);
    }
  }, [selectedDate]);

  const fetchTripsForDate = async (date) => {
    setLoadingDateTrips(true);
    try {
      // IMPORTANT: Folosim format local, NU toISOString() care convertește la UTC și poate schimba ziua!
      const pad = (n) => n.toString().padStart(2, '0');
      const dateStr = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
      const res = await axios.get(
        `${API}/api/trips?start_date=${dateStr}&end_date=${dateStr}`,
        getAuthHeaders()
      );
      setDateTrips(res.data || []);
    } catch (e) {
      toast.error('Eroare la încărcarea curselor');
      setDateTrips([]);
    } finally {
      setLoadingDateTrips(false);
    }
  };

  const filteredTrips = useMemo(() => {
    if (!searchText.trim()) return activeTrips;
    const search = searchText.toLowerCase();
    return activeTrips.filter(trip =>
      trip.car_license?.toLowerCase().includes(search) ||
      trip.car_name?.toLowerCase().includes(search) ||
      trip.driver_name?.toLowerCase().includes(search)
    );
  }, [activeTrips, searchText]);

  const isTestCar = (car) => {
    const name = (car.name || '').toLowerCase();
    const plate = (car.license_plate || '').toLowerCase();
    return name.includes('test') || plate.includes('test');
  };

  const realCars = useMemo(() => cars.filter(car => !isTestCar(car)), [cars]);

  const availableCars = useMemo(() => {
    const carsWithActiveTrips = new Set(activeTrips.map(trip => trip.car_id).filter(Boolean));
    return realCars.filter(car =>
      !carsWithActiveTrips.has(car.id) &&
      car.status !== 'damaged' &&
      car.status !== 'nonavailable'
    );
  }, [realCars, activeTrips]);

  const realCarsInTrip = useMemo(() => {
    const carsWithActiveTrips = new Set(activeTrips.map(trip => trip.car_id).filter(Boolean));
    return realCars.filter(car => carsWithActiveTrips.has(car.id)).length;
  }, [realCars, activeTrips]);

  // Sort date trips: active first, then completed by end_time desc
  const sortedDateTrips = useMemo(() => {
    return [...dateTrips].sort((a, b) => {
      if (a.status === 'completed' && b.status !== 'completed') return 1;
      if (a.status !== 'completed' && b.status === 'completed') return -1;
      if (a.status === 'completed' && b.status === 'completed') {
        const endA = a.end_time ? new Date(a.end_time).getTime() : 0;
        const endB = b.end_time ? new Date(b.end_time).getTime() : 0;
        return endB - endA;
      }
      const startA = a.start_time ? new Date(a.start_time).getTime() : 0;
      const startB = b.start_time ? new Date(b.start_time).getTime() : 0;
      return startB - startA;
    });
  }, [dateTrips]);

  const formatDateRo = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  };

  const completedCount = dateTrips.filter(t => t.status === 'completed').length;
  const activeCount = dateTrips.filter(t => t.status !== 'completed').length;

  return (
    <div className="space-y-4">
      {/* Curse Active Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Map className="w-5 h-5" />
            Curse Active - Monitorizare în Timp Real
          </CardTitle>
          <CardDescription className="text-xs">
            Mașini în producție și șoferi activi (actualizare automată la 30 secunde)
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          {activeTrips.length > 0 && (
            <div className="mb-4 flex items-center gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Caută după mașină sau șofer..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="pl-9 bg-white"
                  data-testid="curse-active-search"
                />
                {searchText && (
                  <button onClick={() => setSearchText('')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              {searchText && (
                <span className="text-sm text-slate-500">
                  {filteredTrips.length} din {activeTrips.length} curse
                </span>
              )}
            </div>
          )}

          {activeTrips.length === 0 ? (
            <div className="text-center py-12 text-slate-600">
              <Map className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">Nu există curse active</p>
              <p className="text-sm mt-2">Toate mașinile sunt disponibile</p>
            </div>
          ) : filteredTrips.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nu s-au găsit curse pentru "{searchText}"</p>
              <Button variant="link" size="sm" onClick={() => setSearchText('')}>Resetează căutarea</Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="text-xs bg-slate-50">
                    <TableHead className="py-2 px-2 font-semibold">Șofer</TableHead>
                    <TableHead className="py-2 px-2 font-semibold">Mașină</TableHead>
                    <TableHead className="py-2 px-2 font-semibold">Traseu</TableHead>
                    <TableHead className="py-2 px-2 font-semibold">Km Start</TableHead>
                    <TableHead className="py-2 px-2 font-semibold">Data</TableHead>
                    {(auth?.user?.role === 'admin' || auth?.user?.roles?.includes('admin')) && (
                      <TableHead className="py-2 px-2 font-semibold">Ora</TableHead>
                    )}
                    <TableHead className="py-2 px-2 font-semibold">Status</TableHead>
                    <TableHead className="py-2 px-2 font-semibold"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTrips.map((trip) => {
                    const bgColor = trip.alert_level === 'critical' ? 'bg-red-50' : trip.alert_level === 'warning' ? 'bg-blue-50' : 'bg-green-50';
                    const badgeColor = trip.alert_level === 'critical' ? 'bg-red-100 text-red-700 border-red-300' : trip.alert_level === 'warning' ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-green-100 text-green-700 border-green-300';
                    const statusIcon = trip.alert_level === 'critical' ? '🔴' : trip.alert_level === 'warning' ? '🔵' : '🟢';
                    
                    return (
                      <TableRow key={trip.id} className={`${bgColor} text-sm`}>
                        <TableCell className="py-2 px-2 font-medium">{trip.driver_name}</TableCell>
                        <TableCell className="py-2 px-2">
                          <div className="font-semibold leading-tight">{trip.car_license}</div>
                        </TableCell>
                        <TableCell className="py-2 px-2 text-sm max-w-[200px] truncate">
                          {trip.departure_location && trip.destination
                            ? `${trip.departure_location} → ${trip.destination}`
                            : trip.route || trip.departure_location || 'N/A'}
                        </TableCell>
                        <TableCell className="py-2 px-2 font-mono font-semibold text-sm">
                          {trip.start_km ? `${trip.start_km.toLocaleString('ro-RO')} km` : 'N/A'}
                        </TableCell>
                        <TableCell className="py-2 px-2 text-xs">
                          {new Date(trip.start_time).toLocaleDateString('ro-RO', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </TableCell>
                        {(auth?.user?.role === 'admin' || auth?.user?.roles?.includes('admin')) && (
                          <TableCell className="py-2 px-2 text-xs font-mono">
                            {new Date(trip.start_time).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                          </TableCell>
                        )}
                        <TableCell className="py-2 px-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${badgeColor}`}>{statusIcon}</span>
                        </TableCell>
                        <TableCell className="py-2 px-2">
                          {auth?.user?.role !== 'dispatcher' && (
                            <Button size="sm" variant="outline" onClick={() => handleOpenCloseDialog(trip)} disabled={loading} className="text-xs h-7 px-2">
                              Închide
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sumar Flotă */}
      <div className="p-4 bg-slate-100 rounded-lg border border-slate-200 space-y-2">
        <div className="text-sm text-slate-700">
          <span className="font-medium">Flotă:</span>{' '}
          <span>{realCars.length} total</span>{' | '}
          <span className="text-blue-600">{realCarsInTrip} în cursă</span>{' | '}
          <span className="text-green-600">{availableCars.length} disponibile</span>
        </div>
        <div className="text-sm">
          <span className="font-medium text-slate-700">Disponibile:</span>{' '}
          {availableCars.length > 0 ? (
            <span className="text-green-700">{availableCars.map(car => car.license_plate).join(', ')}</span>
          ) : (
            <span className="text-slate-500 italic">Toate mașinile sunt în curse active</span>
          )}
        </div>
      </div>

      {/* Istoric Curse pe Zi - cu Calendar */}
      {auth?.user?.role === 'admin' && (
        <Card data-testid="istoric-curse-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CalendarDays className="w-5 h-5 text-indigo-600" />
                  Istoric Curse pe Zi
                </CardTitle>
                <CardDescription className="text-xs">
                  Selectați o dată din calendar pentru a vedea toate cursele (deschise și închise)
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="gap-2 min-w-[200px] justify-start text-left font-normal"
                      data-testid="select-date-btn"
                    >
                      <CalendarDays className="w-4 h-4" />
                      {selectedDate ? formatDateRo(selectedDate) : 'Selectează o dată...'}
                      <ChevronDown className="w-4 h-4 ml-auto opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        setCalendarOpen(false);
                      }}
                      disabled={(date) => date > new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                {selectedDate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedDate(null); setDateTrips([]); }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {!selectedDate ? (
              <div className="text-center py-10 text-slate-400">
                <CalendarDays className="w-14 h-14 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Selectați o dată din calendar pentru a vedea cursele</p>
              </div>
            ) : loadingDateTrips ? (
              <div className="text-center py-10 text-slate-400">
                <p className="text-sm">Se încarcă cursele...</p>
              </div>
            ) : dateTrips.length === 0 ? (
              <div className="text-center py-10 text-slate-400">
                <Map className="w-14 h-14 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Nu au fost curse în data de {formatDateRo(selectedDate)}</p>
              </div>
            ) : (
              <>
                {/* Summary badges */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-sm font-medium text-slate-700">
                    {formatDateRo(selectedDate)}
                  </span>
                  <span className="px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border">
                    {dateTrips.length} curse total
                  </span>
                  {completedCount > 0 && (
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                      {completedCount} finalizate
                    </span>
                  )}
                  {activeCount > 0 && (
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-100 text-blue-700 border border-blue-200">
                      {activeCount} active
                    </span>
                  )}
                </div>

                {/* Trips table */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs bg-slate-50">
                        <TableHead className="py-2 px-2 font-semibold">Șofer</TableHead>
                        <TableHead className="py-2 px-2 font-semibold">Mașină</TableHead>
                        <TableHead className="py-2 px-2 font-semibold">Traseu</TableHead>
                        <TableHead className="py-2 px-2 font-semibold">Km Start</TableHead>
                        <TableHead className="py-2 px-2 font-semibold">Km End</TableHead>
                        <TableHead className="py-2 px-2 font-semibold">Km Parc.</TableHead>
                        <TableHead className="py-2 px-2 font-semibold">Ora Start</TableHead>
                        <TableHead className="py-2 px-2 font-semibold">Ora End</TableHead>
                        <TableHead className="py-2 px-2 font-semibold">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedDateTrips.map((trip, idx) => {
                        const isCompleted = trip.status === 'completed';
                        const startTime = trip.start_time ? new Date(trip.start_time).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : '-';
                        const endTime = trip.end_time ? new Date(trip.end_time).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : '-';
                        const kmParcursi = (trip.start_km && trip.end_km) ? (trip.end_km - trip.start_km) : null;
                        const driverName = trip.driver_full_name || trip.driver_name || '-';
                        const plate = trip.car_license || trip.car_name || '-';
                        const route = trip.departure_location && trip.destination
                          ? `${trip.departure_location} → ${trip.destination}`
                          : trip.route || trip.departure_location || '-';

                        return (
                          <TableRow
                            key={trip.id || idx}
                            className={`text-sm ${isCompleted ? 'bg-green-50/50' : 'bg-blue-50/50'}`}
                          >
                            <TableCell className="py-2 px-2 font-medium">{driverName}</TableCell>
                            <TableCell className="py-2 px-2 font-semibold">{plate}</TableCell>
                            <TableCell className="py-2 px-2 text-sm max-w-[180px] truncate">{route}</TableCell>
                            <TableCell className="py-2 px-2 font-mono text-sm">
                              {trip.start_km ? trip.start_km.toLocaleString('ro-RO') : '-'}
                            </TableCell>
                            <TableCell className="py-2 px-2 font-mono text-sm">
                              {trip.end_km ? trip.end_km.toLocaleString('ro-RO') : '-'}
                            </TableCell>
                            <TableCell className="py-2 px-2 font-mono text-sm font-semibold">
                              {kmParcursi !== null && kmParcursi >= 0 ? kmParcursi.toLocaleString('ro-RO') : '-'}
                            </TableCell>
                            <TableCell className="py-2 px-2 font-mono text-xs">{startTime}</TableCell>
                            <TableCell className="py-2 px-2 font-mono text-xs">{endTime}</TableCell>
                            <TableCell className="py-2 px-2">
                              {isCompleted ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                                  <CheckCircle className="w-3 h-3" /> Închisă
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                  <Clock className="w-3 h-3" /> Activă
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Total km for the day */}
                {dateTrips.some(t => t.start_km && t.end_km) && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg border text-sm">
                    <span className="font-semibold text-slate-700">Total km parcurși: </span>
                    <span className="font-mono font-bold text-indigo-700">
                      {dateTrips
                        .filter(t => t.start_km && t.end_km && (t.end_km - t.start_km) >= 0)
                        .reduce((sum, t) => sum + (t.end_km - t.start_km), 0)
                        .toLocaleString('ro-RO')} km
                    </span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default CurseActiveTab;
